import { dateLib } from "@agentcall/core/lib"
import type { QueryObserverResult, RefetchOptions } from "@tanstack/react-query"
import { memo, useState } from "react"
import { AlertCircle, Edit, Shield, User } from "react-feather"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { getOperationRuleDetailPath, getOperationRuleSummary } from "@/api/client/operation-rule"
import { type OwnerModels, ownerApi } from "@/api/client/owner"
import { getErrorMessage } from "@/api/instance"
import { Button } from "@/components/ui/button"
import { EmptyCard } from "@/components/ui/card"
import { OwnerAdminMemoEditModal } from "./OwnerAdminMemoEditModal"

interface Props {
  // withOperationRules 付きで取得したオーナー(operationRules を含む)
  owner: OwnerModels.OwnerGet200Owner
  refetch: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<OwnerModels.OwnerGet200, unknown>>
}

const OwnerInfoTabComponent = ({ owner, refetch }: Props) => {
  const navigate = useNavigate()
  const [isAdminMemoEditModalOpen, setIsAdminMemoEditModalOpen] = useState(false)

  // 運用ルール一覧: 一番上に既定ルール、その下に物件固有ルール(API が既定→物件ID昇順で返す)(E-13)。
  // 物件名は同じ取得で展開済みの owner.properties から引く。
  const operationRules = owner.operationRules ?? []
  const defaultRule = operationRules.find((rule) => rule.propertyId == null) ?? null
  const propertyNameById = new Map(
    (owner.properties ?? []).map((property) => [property.id, property.name])
  )
  const operationRuleRows = [
    {
      key: "default",
      propertyId: null,
      label: "既定（すべての物件に適用）",
      operationRule: defaultRule,
    },
    ...operationRules.flatMap((rule) =>
      rule.propertyId == null
        ? []
        : [
            {
              key: `property-${rule.propertyId}`,
              propertyId: rule.propertyId,
              label: propertyNameById.get(rule.propertyId) ?? `物件ID: ${rule.propertyId}`,
              operationRule: rule,
            },
          ]
    ),
  ]

  const ownerUpdateAdminMemoMutation = ownerApi.useOwnerUpdateAdminMemo({
    mutation: {
      onSuccess: () => {
        refetch()
        toast.success("オーナー注意事項を更新しました")
      },
      onError: (error) => {
        toast.error("オーナー注意事項の更新に失敗しました", { description: getErrorMessage(error) })
      },
    },
  })

  const handleSubmitMemoEdit = async (data: { adminMemo?: string }) => {
    await ownerUpdateAdminMemoMutation.mutateAsync({
      id: owner.id,
      data: { adminMemo: data.adminMemo || "" },
    })
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">オーナー詳細</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="space-y-3.5 mt-3">
        {/* 概要 Card */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="flex items-center justify-between p-5 border-b border-slate-200">
            <div className="flex items-center gap-1.5">
              <User size={14} />
              <span className="text-sm font-semibold text-slate-600">オーナー情報</span>
            </div>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-5 gap-x-7.5 gap-y-4">
              {/* 上の行 - 5つ */}
              <div>
                <div className="text-xs font-medium text-slate-400 mb-2">ID</div>
                <div className="text-sm text-slate-600">{owner.id}</div>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-400 mb-2">アカウント作成日時</div>
                <div className="text-sm text-slate-600">
                  {owner.createdAt ? dateLib.formatDateTime(owner.createdAt) : "-"}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-400 mb-2">氏名</div>
                <div className="text-sm text-slate-600">{owner.name}</div>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-400 mb-2">氏名（カナ）</div>
                <div className="text-sm text-slate-600">
                  {owner.lastNameKana} {owner.firstNameKana}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-400 mb-2">電話番号</div>
                <div className="text-sm text-slate-600">{owner.phoneNumber}</div>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-400 mb-2">
                  緊急用電話番号（任意）
                </div>
                <div className="text-sm text-slate-600">{owner.emergencyPhoneNumber || "-"}</div>
              </div>

              {/* 下の行 - 4つ */}
              <div>
                <div className="text-xs font-medium text-slate-400 mb-2">メールアドレス</div>
                <div className="text-sm text-slate-600">{owner.email}</div>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-400 mb-2">メール認証</div>
                <div className="text-sm text-slate-600">
                  {owner.emailVerified ? "認証済み" : "未認証"}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-400 mb-2">住所</div>
                <div className="text-sm text-slate-600">{owner.address}</div>
              </div>
              <div className="col-start-1">
                <div className="text-xs font-medium text-slate-400 mb-2">EchoesオーナーID</div>
                <div className="text-sm text-slate-600">{owner.echoesOwnerId ?? "-"}</div>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-400 mb-2">Echoes連携日時</div>
                <div className="text-sm text-slate-600">
                  {owner.echoesLinkedAt ? dateLib.formatDateTime(owner.echoesLinkedAt) : "-"}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-400 mb-2">Echoes最終同期日時</div>
                <div className="text-sm text-slate-600">
                  {owner.echoesUpdatedAt ? dateLib.formatDateTime(owner.echoesUpdatedAt) : "-"}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-400 mb-2">Echoes同期失敗日時</div>
                <div
                  className={`text-sm ${owner.echoesFailedAt ? "text-red-600" : "text-slate-600"}`}
                >
                  {owner.echoesFailedAt ? dateLib.formatDateTime(owner.echoesFailedAt) : "-"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* オーナー注意事項 Card */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-200">
            <div className="flex items-center gap-1.5">
              <AlertCircle className="w-3 h-3" />
              <span className="text-sm font-semibold text-slate-600">オーナー注意事項</span>
            </div>
            <Button variant="outline" size="sm" onClick={() => setIsAdminMemoEditModalOpen(true)}>
              <Edit size={14} className="mr-2" />
              編集
            </Button>
          </div>
          <div className="p-5">
            <div className="text-sm text-slate-600 whitespace-pre-wrap">
              {owner.adminMemo || "注意事項はありません"}
            </div>
          </div>
        </div>
      </div>

      {/* 運用ルール List */}
      <div className="bg-white rounded-lg shadow-sm mt-4">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-1.5">
            <Shield className="w-3 h-3" />
            <span className="text-sm font-semibold text-slate-600">運用ルール</span>
          </div>
          <span className="text-xs text-slate-500">
            物件個別ルールが無い物件には既定ルールが適用されます
          </span>
        </div>
        <div className="p-5">
          <div className="bg-white rounded-lg border border-slate-200">
            <div className="grid grid-cols-[220px_1fr_140px_80px] gap-4 px-5 py-2.5 border-b border-slate-200 text-sm font-semibold text-slate-600">
              <span>適用範囲</span>
              <span>設定内容</span>
              <span>最終更新</span>
              <span />
            </div>
            <ul>
              {operationRuleRows.map((row) => (
                <li
                  key={row.key}
                  className="grid grid-cols-[220px_1fr_140px_80px] gap-4 items-center px-5 py-3 border-b border-slate-200 last:border-b-0"
                >
                  <div className="flex items-center gap-2 text-sm">
                    <span>{row.label}</span>
                    {row.propertyId != null && (
                      <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 rounded px-1.5 py-0.5">
                        個別
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 truncate">
                    {row.operationRule ? getOperationRuleSummary(row.operationRule) : "未設定"}
                  </p>
                  <p className="text-sm text-slate-600">
                    {row.operationRule ? dateLib.formatDateTime(row.operationRule.updatedAt) : "-"}
                  </p>
                  <div className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(getOperationRuleDetailPath(owner.id, row.propertyId))}
                    >
                      詳細
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          {operationRuleRows.length === 1 && !defaultRule && (
            <EmptyCard className="mt-4" text="運用ルールは設定されていません" />
          )}
        </div>
      </div>

      {/* Memo Edit Modal */}
      <OwnerAdminMemoEditModal
        open={isAdminMemoEditModalOpen}
        onOpenChange={setIsAdminMemoEditModalOpen}
        body={owner.adminMemo || ""}
        onSubmit={handleSubmitMemoEdit}
        isLoading={ownerUpdateAdminMemoMutation.isPending}
      />
    </div>
  )
}

export const OwnerInfoTab = memo(OwnerInfoTabComponent)
